import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomainTopicsService, DiagnosticResponse, DiagnosticTopicUrl } from '../../services/domain-topics';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-topic-diagnostic',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './topic-diagnostic.html',
  styleUrl: './topic-diagnostic.css'
})
export class TopicDiagnosticComponent implements OnInit, OnDestroy {
  @Input() domain: string = '';
  @Input() topicSlug: string = '';
  @Input() onClose?: () => void;

  diagnosticData: DiagnosticResponse['data'] | null = null;
  loading = false;
  error: string | null = null;
  expandedBreadthTopicSlug: string | null = null;
  expandedDepthTopicSlug: string | null = null;
  expandedMentionsByTopic = new Set<string>();
  showAllFocusedPages = false;
  showAllContextualLinks = false;

  private destroy$ = new Subject<void>();

  constructor(private domainTopicsService: DomainTopicsService) {}

  ngOnInit() {
    if (this.domain && this.topicSlug) {
      this.loadDiagnosticData();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadDiagnosticData() {
    if (!this.domain.trim() || !this.topicSlug.trim()) {
      this.error = 'Domain and topic slug are required';
      return;
    }

    this.loading = true;
    this.error = null;

    this.domainTopicsService.getTopicDiagnostic(this.domain, this.topicSlug)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.diagnosticData = response.data;
          this.loading = false;
        },
        error: (err) => {
          this.error = err.error?.message || 'Failed to load diagnostic data';
          this.loading = false;
        }
      });
  }

  toggleBreadthTopicUrls(topicSlug: string) {
    if (this.expandedBreadthTopicSlug === topicSlug) {
      this.expandedMentionsByTopic.delete(topicSlug);
      this.expandedBreadthTopicSlug = null;
    } else {
      this.expandedBreadthTopicSlug = topicSlug;
      // Close depth if open
      if (this.expandedDepthTopicSlug) {
        this.expandedMentionsByTopic.delete(this.expandedDepthTopicSlug);
      }
      this.expandedDepthTopicSlug = null;
    }
  }

  toggleDepthTopicUrls(topicSlug: string) {
    if (this.expandedDepthTopicSlug === topicSlug) {
      this.expandedMentionsByTopic.delete(topicSlug);
      this.expandedDepthTopicSlug = null;
    } else {
      this.expandedDepthTopicSlug = topicSlug;
      // Close breadth if open
      if (this.expandedBreadthTopicSlug) {
        this.expandedMentionsByTopic.delete(this.expandedBreadthTopicSlug);
      }
      this.expandedBreadthTopicSlug = null;
    }
  }

  toggleMentions(topicSlug: string) {
    if (this.expandedMentionsByTopic.has(topicSlug)) {
      this.expandedMentionsByTopic.delete(topicSlug);
    } else {
      this.expandedMentionsByTopic.add(topicSlug);
    }
  }

  areMentionsExpanded(topicSlug: string): boolean {
    return this.expandedMentionsByTopic.has(topicSlug);
  }

  private getCoverageTopic(topicSlug: string) {
    if (!this.diagnosticData) return null;

    const directlyCovered = this.diagnosticData.coverage.directlyCoveredTopics.find(
      t => t.parentTopicSlug === topicSlug
    );
    if (directlyCovered) {
      return directlyCovered;
    }

    const related = this.diagnosticData.coverage.relatedTopics.find(
      t => t.parentTopicSlug === topicSlug
    );
    if (related) {
      return related;
    }

    return null;
  }

  getCorePages(topicSlug: string): DiagnosticTopicUrl[] {
    const topic = this.getCoverageTopic(topicSlug);
    if (!topic) return [];
    if (Array.isArray(topic.corePages)) return topic.corePages;
    return topic.topUrls?.filter(url => url.intentBucket === 'core') || [];
  }

  getMentionPages(topicSlug: string): DiagnosticTopicUrl[] {
    const topic = this.getCoverageTopic(topicSlug);
    if (!topic) return [];
    if (Array.isArray(topic.mentionPages)) return topic.mentionPages;
    return topic.topUrls?.filter(url => url.intentBucket === 'mention') || [];
  }

  getMentionCount(topicSlug: string): number {
    const topic = this.getCoverageTopic(topicSlug);
    return topic?.mentionCount || this.getMentionPages(topicSlug).length;
  }

  getCoreCount(topicSlug: string): number {
    const topic = this.getCoverageTopic(topicSlug);
    return topic?.coreCount || this.getCorePages(topicSlug).length;
  }

  formatSeoIntent(score: number | null | undefined): string {
    if (!Number.isFinite(score as number)) return 'n/a';
    return `${Math.round((score as number) * 100)}%`;
  }

  formatSpecificity(score: number | null | undefined): string {
    if (!Number.isFinite(score as number)) return 'n/a';
    return `${Math.round((score as number) * 100)}%`;
  }

  formatRatio(score: number | null | undefined): string {
    if (!Number.isFinite(score as number)) return 'n/a';
    return `${((score as number) * 100).toFixed(1)}%`;
  }

  getTopicLabel(topicSlug: string): string {
    if (!this.diagnosticData) return '';

    // Check directly covered topics
    const directlyCovered = this.diagnosticData.coverage.directlyCoveredTopics.find(
      t => t.parentTopicSlug === topicSlug
    );
    if (directlyCovered) {
      return directlyCovered.label;
    }

    // Check related topics
    const related = this.diagnosticData.coverage.relatedTopics.find(
      t => t.parentTopicSlug === topicSlug
    );
    if (related) {
      return related.label;
    }

    return topicSlug;
  }

  getBreadthLevel(): string {
    if (!this.diagnosticData?.centerTopic?.breadthScore) {
      return 'low';
    }
    const score = this.diagnosticData.centerTopic.breadthScore;
    if (score < 0.33) {
      return 'low';
    } else if (score < 0.66) {
      return 'med';
    } else {
      return 'high';
    }
  }

  getDepthLevel(): string {
    if (!this.diagnosticData?.centerTopic?.depthScore) {
      return 'low';
    }
    const score = this.diagnosticData.centerTopic.depthScore;
    if (score < 0.33) {
      return 'low';
    } else if (score < 0.66) {
      return 'med';
    } else {
      return 'high';
    }
  }

  getTotalRelatedPages(): number {
    if (!this.diagnosticData?.coverage?.relatedTopics) {
      return 0;
    }
    return this.diagnosticData.coverage.relatedTopics.reduce((sum, topic) => sum + topic.pageCount, 0);
  }

  getCoverageLevel(): string {
    if (!this.diagnosticData?.centerTopic?.coverageScore) {
      return 'low';
    }
    const score = this.diagnosticData.centerTopic.coverageScore;
    if (score < 0.33) {
      return 'low';
    } else if (score < 0.66) {
      return 'med';
    } else {
      return 'high';
    }
  }

  getStrengthLevel(): string {
    if (!this.diagnosticData?.strength?.strengthScore) {
      return 'low';
    }
    const score = this.diagnosticData.strength.strengthScore;
    if (score < 0.33) {
      return 'low';
    } else if (score < 0.66) {
      return 'med';
    } else {
      return 'high';
    }
  }

  getFocusLevel(): string {
    if (!this.diagnosticData?.focus?.focusScore) {
      return 'low';
    }
    const score = this.diagnosticData.focus.focusScore;
    if (score < 0.33) {
      return 'low';
    } else if (score < 0.66) {
      return 'med';
    } else {
      return 'high';
    }
  }

  getLinkLevel(): string {
    if (!this.diagnosticData?.links?.linkScore) {
      return 'low';
    }
    const score = this.diagnosticData.links.linkScore;
    if (score < 0.33) {
      return 'low';
    } else if (score < 0.66) {
      return 'med';
    } else {
      return 'high';
    }
  }

  getFocusedPages(): Array<{ url: string; pageId: string; weight: number; salience: number; relativeDensity: number }> {
    if (!this.diagnosticData?.focus?.mostFocusedPages) return [];
    if (this.showAllFocusedPages) {
      return this.diagnosticData.focus.mostFocusedPages;
    }
    return this.diagnosticData.focus.mostFocusedPages.slice(0, 5);
  }

  getContextualLinks(): Array<{ sourceUrl: string; targetUrl: string; anchorText: string; linkType: string }> {
    if (!this.diagnosticData?.links?.topContextualLinks) return [];
    if (this.showAllContextualLinks) {
      return this.diagnosticData.links.topContextualLinks;
    }
    return this.diagnosticData.links.topContextualLinks.slice(0, 5);
  }

  extractUrlPath(url: string): string {
    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname;
      if (path.length > 50) {
        return '...' + path.slice(-47);
      }
      return path || '/';
    } catch {
      const match = url.match(/https?:\/\/[^\/]+(\/.*)/);
      if (match) {
        const path = match[1];
        if (path.length > 50) {
          return '...' + path.slice(-47);
        }
        return path;
      }
      return url.length > 50 ? '...' + url.slice(-47) : url;
    }
  }

  close() {
    if (this.onClose) {
      this.onClose();
    }
  }
}
